import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdAddShoppingCart, MdHistory, MdSave, MdStorage, MdWorkspacePremium } from 'react-icons/md'
import { usePermission } from '@/lib/hooks/use-permission'
import {
    getAuditLogs,
    getSettings,
    getStats,
    getUsage,
    getUsageByOrigin,
    updateSettings,
    type StorageAuditLog,
    type StorageSettings,
    type StorageUsage,
    type StorageUsageByOrigin,
} from '@/lib/api/storage/storage-api'
import { getCustomPackPreview } from '@/lib/api/packs/packs-api'
import { buyPack } from '@/lib/api/subscription-billing/subscription-billing-api'
import { withCheckoutReturn } from '@/lib/billing/checkout-return-url'
import { formatBytes, formatDateTime } from './storage-utils'

const GB = 1024 * 1024 * 1024
const MB = 1024 * 1024

const AUDIT_LABELS: Record<string, string> = {
    VIEW: 'Visualizou',
    DOWNLOAD: 'Baixou',
    UPLOAD: 'Enviou',
    RENAME: 'Renomeou',
    MOVE: 'Moveu',
    DELETE: 'Excluiu',
    RESTORE: 'Restaurou',
    PURGE: 'Excluiu definitivamente',
    SHARE_CREATE: 'Criou link',
    SHARE_ACCESS: 'Acessou por link',
    SHARE_REVOKE: 'Revogou link',
    PASSWORD_OK: 'Desbloqueou',
    PASSWORD_FAIL: 'Errou a senha',
    VERSION_RESTORE: 'Restaurou versão',
}

/**
 * Rótulo de cada origem de arquivo. A chave é o `resource` gravado no upload;
 * origem desconhecida cai no próprio identificador, que ainda diz mais que
 * "Outros" e denuncia recurso novo que esqueceram de nomear aqui.
 */
const ORIGIN_LABELS: Record<string, string> = {
    storage: 'Módulo de Armazenamento',
    messages: 'Anexos do atendimento',
    'internal-chat': 'Anexos do chat interno',
    billings: 'Comprovantes de cobrança',
    users: 'Fotos de usuários',
    organizations: 'Logos da organização',
    clients: 'Documentos de clientes',
    professionals: 'Documentos de profissionais',
    partners: 'Documentos de parceiros',
    establishments: 'Documentos de estabelecimentos',
    products: 'Fotos de produtos',
    store: 'Imagens da loja',
    exams: 'Exames',
    'exam-results': 'Resultados de exames',
    consultations: 'Anexos de consultas',
    orders: 'Anexos de procedimentos',
    'medical-reports': 'Laudos e relatórios',
    sign: 'Documentos para assinatura',
    signed: 'Documentos assinados',
    teleconsultations: 'Gravações de teleconsulta',
    'help-center': 'Central de Ajuda',
    mediators: 'Documentos de mediadores',
    uploads: 'Outros envios',
}

const GROUP_LABELS: Record<string, string> = {
    image: 'Imagens',
    video: 'Vídeos',
    audio: 'Áudios',
    document: 'Documentos',
    other: 'Outros',
}

export default function StorageSettingsPage() {
    const { canUpdate } = usePermission()
    const canEdit = canUpdate('storage', 'settings')

    const [settings, setSettings] = useState<StorageSettings | null>(null)
    const [usage, setUsage] = useState<StorageUsage | null>(null)
    const [stats, setStats] = useState<{ group: string; count: number; sizeBytes: string }[]>([])
    const [logs, setLogs] = useState<StorageAuditLog[]>([])
    const [byOrigin, setByOrigin] = useState<StorageUsageByOrigin[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)


    // Compra de espaço adicional.
    const [extraGb, setExtraGb] = useState(10)
    const [extraPrice, setExtraPrice] = useState<number | null>(null)
    const [hiringStorage, setHiringStorage] = useState(false)

    /**
     * Contratar espaço gera a COBRANÇA do pacote e leva ao checkout público
     * dela — o mesmo caminho da mensalidade. O pagamento confirmado é quem abre
     * a recorrência mensal que sustenta o espaço; a partir daí a renovação vem
     * como cobrança todo mês, sem cartão guardado.
     */
    const handleHireStorage = async () => {
        setHiringStorage(true)
        try {
            const response = await buyPack({
                type: 'storage-gb',
                quantity: extraGb,
            })
            if (response.success && response.result?.payUrl) {
                window.location.href = withCheckoutReturn(
                    response.result.payUrl,
                    'app',
                )
                return
            }
            toast.error(
                response.error ||
                    'Não foi possível gerar a cobrança do armazenamento adicional',
            )
        } catch {
            toast.error('Erro ao gerar a cobrança do armazenamento adicional')
        } finally {
            setHiringStorage(false)
        }
    }

    // Formulário em unidades amigáveis; a API trabalha em bytes.
    const [limitGb, setLimitGb] = useState(0)
    const [useSelfLimit, setUseSelfLimit] = useState(false)
    const [trashDays, setTrashDays] = useState(30)
    const [unlockMinutes, setUnlockMinutes] = useState(30)
    const [allowPublicShares, setAllowPublicShares] = useState(true)
    const [shareExpiryDays, setShareExpiryDays] = useState(7)

    const load = useCallback(async () => {
        setLoading(true)
        const [
            settingsResponse,
            usageResponse,
            statsResponse,
            logsResponse,
            originResponse,
        ] = await Promise.all([
            getSettings(),
            getUsage(),
            getStats(),
            getAuditLogs({ limit: 30 }),
            getUsageByOrigin(),
        ])

        if (settingsResponse.success && settingsResponse.result) {
            const value = settingsResponse.result
            setSettings(value)
            setUseSelfLimit(value.selfLimitBytes != null)
            setLimitGb(
                value.selfLimitBytes ? Math.round(Number(value.selfLimitBytes) / GB) : 0,
            )
            setTrashDays(value.trashRetentionDays)
            setUnlockMinutes(Math.round(value.unlockTtlSeconds / 60))
            setAllowPublicShares(value.allowPublicShares)
            setShareExpiryDays(value.shareDefaultExpiryDays)
        }

        if (usageResponse.success) setUsage(usageResponse.result ?? null)
        if (statsResponse.success) setStats(statsResponse.result ?? [])
        if (logsResponse.success) setLogs(logsResponse.result ?? [])
        if (originResponse.success) setByOrigin(originResponse.result ?? [])

        setLoading(false)
    }, [])

    useEffect(() => {
        void load()
    }, [load])

    // Preço do GB adicional vem do mesmo cálculo do checkout, para a tela não
    // exibir um valor que a cobrança depois contradiz.
    useEffect(() => {
        let active = true

        void (async () => {
            const response = await getCustomPackPreview('storage-gb', extraGb)
            if (!active) return
            setExtraPrice(response.success ? (response.result?.price ?? null) : null)
        })()

        return () => {
            active = false
        }
    }, [extraGb])

    const handleSave = async () => {
        setSaving(true)
        const response = await updateSettings({
            selfLimitBytes: useSelfLimit ? limitGb * GB : null,
            trashRetentionDays: trashDays,
            unlockTtlSeconds: unlockMinutes * 60,
            allowPublicShares,
            shareDefaultExpiryDays: shareExpiryDays,
        })
        setSaving(false)

        if (!response.success) {
            toast.error(response.error || 'Não foi possível salvar as configurações')
            return
        }

        toast.success('Configurações salvas com sucesso')
        setSettings(response.result ?? settings)
        void load()
    }

    if (loading) {
        return (
            <div className="flex justify-center py-20">
                <span className="loading loading-spinner loading-lg text-primary" />
            </div>
        )
    }

    return (
        <div className="p-4 sm:p-6 space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Configurações do Armazenamento</h1>
                <p className="text-sm text-base-content/60 mt-0.5">
                    Cota, limites de envio, retenção da lixeira e links públicos
                </p>
            </div>

            {usage && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="card bg-base-100 border border-base-300">
                        <div className="card-body p-4">
                            <div className="flex items-center gap-2 text-base-content/60 text-sm">
                                <MdStorage />
                                Espaço utilizado
                            </div>
                            <p className="text-2xl font-semibold">
                                {formatBytes(usage.usedBytes)}
                            </p>
                            <progress
                                className={`progress w-full h-1.5 ${
                                    usage.percent >= 90
                                        ? 'progress-error'
                                        : usage.percent >= 75
                                          ? 'progress-warning'
                                          : 'progress-primary'
                                }`}
                                value={usage.percent}
                                max={100}
                            />
                            <p className="text-xs text-base-content/50">
                                {usage.unlimited
                                    ? 'sem limite de espaço'
                                    : `de ${formatBytes(usage.limitBytes ?? '0')}`}
                            </p>
                        </div>
                    </div>

                    <div className="card bg-base-100 border border-base-300">
                        <div className="card-body p-4">
                            <span className="text-sm text-base-content/60">Arquivos</span>
                            <p className="text-2xl font-semibold">{usage.fileCount}</p>
                        </div>
                    </div>

                    <div className="card bg-base-100 border border-base-300">
                        <div className="card-body p-4">
                            <span className="text-sm text-base-content/60">Por tipo</span>
                            <div className="space-y-1 mt-1">
                                {stats.length ? (
                                    stats.map(item => (
                                        <div
                                            key={item.group}
                                            className="flex justify-between text-sm"
                                        >
                                            <span className="text-base-content/70">
                                                {GROUP_LABELS[item.group] ?? item.group}
                                            </span>
                                            <span>{formatBytes(item.sizeBytes)}</span>
                                        </div>
                                    ))
                                ) : (
                                    <span className="text-sm text-base-content/50">
                                        Nenhum arquivo enviado
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Responde "por que estou usando este espaço". O consumo inclui
                tudo que grava no bucket — anexo de atendimento, comprovante,
                foto —, e não só o que foi enviado pelo módulo. */}
            {byOrigin.length > 0 && (
                <div className="card bg-base-100 border border-base-300">
                    <div className="card-body p-5 sm:p-6">
                        <h2 className="text-lg font-semibold mb-1">O que ocupa o espaço</h2>
                        <p className="text-sm text-base-content/60 mb-5">
                            Todo arquivo do sistema conta, não apenas os enviados pelo
                            Armazenamento.
                        </p>

                        <div className="space-y-3">
                            {byOrigin.map(item => {
                                const total = byOrigin.reduce(
                                    (sum, row) => sum + Number(row.sizeBytes),
                                    0,
                                )
                                const share = total
                                    ? Math.round((Number(item.sizeBytes) / total) * 100)
                                    : 0

                                return (
                                    <div key={item.resource}>
                                        <div className="flex items-baseline justify-between gap-3 text-sm">
                                            <span className="font-medium">
                                                {ORIGIN_LABELS[item.resource] ??
                                                    item.resource}
                                            </span>
                                            <span className="text-base-content/60 whitespace-nowrap">
                                                {formatBytes(item.sizeBytes)}
                                                <span className="text-base-content/40">
                                                    {' '}
                                                    ·{' '}
                                                    {item.count.toLocaleString('pt-BR')}{' '}
                                                    {item.count === 1
                                                        ? 'arquivo'
                                                        : 'arquivos'}
                                                </span>
                                            </span>
                                        </div>
                                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-base-300">
                                            <div
                                                className="h-full rounded-full bg-primary"
                                                style={{ width: `${share}%` }}
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}

            {usage && (
                <div className="card bg-base-100 border border-base-300">
                    <div className="card-body p-5 sm:p-6">
                        <h2 className="text-lg font-semibold flex items-center gap-2 mb-1">
                            <MdWorkspacePremium className="text-primary" />
                            Espaço do plano
                        </h2>
                        <p className="text-sm text-base-content/60 mb-5">
                            O teto do Armazenamento vem do plano contratado. Para ampliá-lo,
                            contrate GB adicionais.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                            <div className="p-4 rounded-lg bg-base-200">
                                <span className="text-xs text-base-content/60">
                                    Incluído no plano
                                </span>
                                <p className="text-xl font-semibold">
                                    {usage.planGb === 'unlimited'
                                        ? 'Ilimitado'
                                        : `${usage.planGb} GB`}
                                </p>
                            </div>
                            <div className="p-4 rounded-lg bg-base-200">
                                <span className="text-xs text-base-content/60">
                                    Adicional contratado
                                </span>
                                <p className="text-xl font-semibold">
                                    {usage.additionalGb} GB
                                </p>
                            </div>
                            <div className="p-4 rounded-lg bg-base-200">
                                <span className="text-xs text-base-content/60">
                                    Total disponível
                                </span>
                                <p className="text-xl font-semibold">
                                    {usage.planLimitBytes
                                        ? formatBytes(usage.planLimitBytes)
                                        : 'Ilimitado'}
                                </p>
                            </div>
                        </div>

                        {usage.planGb !== 'unlimited' && (
                            <div className="border border-base-300 rounded-lg p-4">
                                <h3 className="font-medium flex items-center gap-2 mb-3">
                                    <MdAddShoppingCart />
                                    Contratar espaço adicional
                                </h3>

                                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                                    <div className="form-control w-full sm:max-w-[12rem]">
                                        <label
                                            className="label pb-1.5"
                                            htmlFor="storage-extra-gb"
                                        >
                                            <span className="label-text font-medium">
                                                Quantidade
                                            </span>
                                        </label>
                                        <div className="join w-full">
                                            <input
                                                id="storage-extra-gb"
                                                type="number"
                                                min={1}
                                                className="input input-bordered join-item w-full"
                                                value={extraGb}
                                                onChange={event =>
                                                    setExtraGb(
                                                        Math.max(
                                                            1,
                                                            Number(event.target.value) || 1,
                                                        ),
                                                    )
                                                }
                                            />
                                            <span className="btn join-item btn-ghost border border-base-300 pointer-events-none">
                                                GB
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex-1">
                                        <p className="text-sm text-base-content/60">
                                            Valor mensal
                                        </p>
                                        <p className="text-xl font-semibold">
                                            {extraPrice != null
                                                ? (extraPrice / 100).toLocaleString('pt-BR', {
                                                      style: 'currency',
                                                      currency: 'BRL',
                                                  })
                                                : '--'}
                                        </p>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={
                                            extraPrice == null ||
                                            extraPrice <= 0 ||
                                            hiringStorage
                                        }
                                        onClick={handleHireStorage}
                                    >
                                        {hiringStorage ? (
                                            <span className="loading loading-spinner loading-sm" />
                                        ) : (
                                            <MdAddShoppingCart />
                                        )}
                                        Contratar
                                    </button>
                                </div>

                                <p className="text-xs text-base-content/60 mt-3">
                                    O espaço adicional é mensal: o arquivo continua ocupando
                                    disco no mês seguinte, então uma cobrança nova é emitida a
                                    cada mês enquanto o adicional estiver contratado. Nenhum
                                    arquivo é perdido se a renovação não for paga — apenas não
                                    será possível enviar novos até regularizar.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="card bg-base-100 border border-base-300">
                <div className="card-body p-5 sm:p-6">
                    <h2 className="text-lg font-semibold mb-5">Limites</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                        {/* Teto próprio: serve para a organização se conter
                            abaixo do plano. O plano continua sendo o limite
                            máximo — o backend recusa valor acima dele. */}
                        <div className="form-control w-full md:col-span-2">
                            <label className="label cursor-pointer justify-start gap-3 py-0 mb-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-primary"
                                    checked={useSelfLimit}
                                    disabled={!canEdit}
                                    onChange={event => {
                                        setUseSelfLimit(event.target.checked)
                                        if (event.target.checked && !limitGb) {
                                            setLimitGb(
                                                usage?.planLimitBytes
                                                    ? Math.max(
                                                          1,
                                                          Math.round(
                                                              Number(usage.planLimitBytes) / GB,
                                                          ),
                                                      )
                                                    : 1,
                                            )
                                        }
                                    }}
                                />
                                <span className="label-text">
                                    Definir um limite próprio, menor que o do plano
                                </span>
                            </label>

                            {useSelfLimit && (
                                <div className="max-w-xs">
                                    <div className="join w-full">
                                        <input
                                            id="storage-self-limit"
                                            type="number"
                                            min={1}
                                            max={
                                                usage?.planLimitBytes
                                                    ? Math.round(
                                                          Number(usage.planLimitBytes) / GB,
                                                      )
                                                    : undefined
                                            }
                                            className="input input-bordered join-item w-full"
                                            value={limitGb}
                                            disabled={!canEdit}
                                            onChange={event =>
                                                setLimitGb(Number(event.target.value))
                                            }
                                        />
                                        <span className="btn join-item btn-ghost border border-base-300 pointer-events-none">
                                            GB
                                        </span>
                                    </div>
                                    <span className="label-text-alt text-base-content/50 mt-1.5 block">
                                        {usage?.planLimitBytes
                                            ? `Máximo do plano: ${formatBytes(
                                                  usage.planLimitBytes,
                                              )}`
                                            : 'O plano não impõe limite de espaço'}
                                    </span>
                                </div>
                            )}

                            {!useSelfLimit && (
                                <span className="label-text-alt text-base-content/50">
                                    Sem limite próprio, vale todo o espaço do plano
                                </span>
                            )}
                        </div>

                        {/* Informativo: o limite por arquivo vem do plano, não da
                            organização — poder editá-lo aqui anularia o limite. */}
                        <div className="form-control w-full">
                            <label className="label pb-1.5">
                                <span className="label-text font-medium">
                                    Tamanho máximo por arquivo
                                </span>
                            </label>
                            <div className="rounded-lg bg-base-200 px-4 py-3">
                                <p className="font-medium">
                                    {settings?.maxFileBytes
                                        ? formatBytes(settings.maxFileBytes)
                                        : 'Sem limite por arquivo'}
                                </p>
                                <p className="text-xs text-base-content/60 mt-0.5">
                                    Definido pelo seu plano
                                </p>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="storage-trash">
                                <span className="label-text font-medium">
                                    Retenção da lixeira
                                </span>
                            </label>
                            <div className="join w-full">
                                <input
                                    id="storage-trash"
                                    type="number"
                                    min={1}
                                    className="input input-bordered join-item w-full"
                                    value={trashDays}
                                    disabled={!canEdit}
                                    onChange={event => setTrashDays(Number(event.target.value))}
                                />
                                <span className="btn join-item btn-ghost border border-base-300 pointer-events-none">
                                    dias
                                </span>
                            </div>
                            <span className="label-text-alt text-base-content/50 mt-1.5">
                                Depois desse prazo o item é excluído definitivamente
                            </span>
                        </div>

                        <div className="form-control w-full">
                            <label className="label pb-1.5" htmlFor="storage-unlock">
                                <span className="label-text font-medium">
                                    Validade do desbloqueio por senha
                                </span>
                            </label>
                            <div className="join w-full">
                                <input
                                    id="storage-unlock"
                                    type="number"
                                    min={1}
                                    className="input input-bordered join-item w-full"
                                    value={unlockMinutes}
                                    disabled={!canEdit}
                                    onChange={event =>
                                        setUnlockMinutes(Number(event.target.value))
                                    }
                                />
                                <span className="btn join-item btn-ghost border border-base-300 pointer-events-none">
                                    minutos
                                </span>
                            </div>
                            <span className="label-text-alt text-base-content/50 mt-1.5">
                                Após esse tempo a senha é exigida novamente
                            </span>
                        </div>

                        {/* Informativo: a lista é política da plataforma. Se a
                            empresa pudesse editar, a proteção não existiria. */}
                        <div className="form-control w-full md:col-span-2">
                            <label className="label pb-1.5">
                                <span className="label-text font-medium">
                                    Extensões bloqueadas
                                </span>
                            </label>
                            <div className="rounded-lg bg-base-200 px-4 py-3">
                                <div className="flex flex-wrap gap-1.5">
                                    {(settings?.blockedExtensions ?? []).map(extension => (
                                        <span
                                            key={extension}
                                            className="badge badge-sm font-mono"
                                        >
                                            {extension}
                                        </span>
                                    ))}
                                </div>
                                <p className="text-xs text-base-content/60 mt-2">
                                    Definidas pela plataforma. Executável em pasta
                                    compartilhada é vetor de ransomware.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card bg-base-100 border border-base-300">
                <div className="card-body p-5 sm:p-6">
                    <h2 className="text-lg font-semibold mb-5">Links públicos</h2>

                    <div className="space-y-5">
                        <label className="label cursor-pointer justify-start gap-3 py-0">
                            <input
                                type="checkbox"
                                className="toggle toggle-primary"
                                checked={allowPublicShares}
                                disabled={!canEdit}
                                onChange={event => setAllowPublicShares(event.target.checked)}
                            />
                            <span className="label-text">
                                Permitir gerar links públicos de arquivos
                            </span>
                        </label>

                        <div className="form-control w-full max-w-xs">
                            <label className="label pb-1.5" htmlFor="storage-share-expiry">
                                <span className="label-text font-medium">
                                    Validade padrão do link
                                </span>
                            </label>
                            <div className="join w-full">
                                <input
                                    id="storage-share-expiry"
                                    type="number"
                                    min={0}
                                    className="input input-bordered join-item w-full"
                                    value={shareExpiryDays}
                                    disabled={!canEdit || !allowPublicShares}
                                    onChange={event =>
                                        setShareExpiryDays(Number(event.target.value))
                                    }
                                />
                                <span className="btn join-item btn-ghost border border-base-300 pointer-events-none">
                                    dias
                                </span>
                            </div>
                            <span className="label-text-alt text-base-content/50 mt-1.5">
                                0 permite links sem expiração
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {canEdit && (
                <div className="flex justify-end">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdSave />
                        )}
                        Salvar configurações
                    </button>
                </div>
            )}

            <div className="card bg-base-100 border border-base-300">
                <div className="card-body p-5 sm:p-6">
                    <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
                        <MdHistory />
                        Atividade recente
                    </h2>

                    {!logs.length ? (
                        <p className="text-sm text-base-content/60">
                            Nenhuma atividade registrada
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th className="w-48">Quando</th>
                                        <th>Usuário</th>
                                        <th>Ação</th>
                                        <th className="w-32">Item</th>
                                        <th className="w-36">IP</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map(log => (
                                        <tr key={log.id} className="hover">
                                            <td className="text-base-content/60">
                                                {formatDateTime(log.createdAt)}
                                            </td>
                                            <td>{log.userName || 'Acesso externo'}</td>
                                            <td>
                                                <span
                                                    className={`badge badge-sm ${
                                                        log.action === 'PASSWORD_FAIL'
                                                            ? 'badge-error'
                                                            : 'badge-ghost'
                                                    }`}
                                                >
                                                    {AUDIT_LABELS[log.action] ?? log.action}
                                                </span>
                                            </td>
                                            <td className="font-mono text-xs">
                                                {log.fileId
                                                    ? `#${log.fileId}`
                                                    : log.folderId
                                                      ? `Pasta #${log.folderId}`
                                                      : '-'}
                                            </td>
                                            <td className="text-base-content/50 font-mono text-xs">
                                                {log.ip || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
